import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { Stack, useTheme } from "@mui/material";

import useGetAgents from "../../hooks/agents/useGetAgents";
import useDeleteAgent from "../../hooks/agents/useDeleteAgent";
import useDeleteAllAgents from "../../hooks/agents/useDeleteAllAgents";
import useRestoreAgent from "../../hooks/agents/useRestoreAgent";
import useRestoreAllAgents from "../../hooks/agents/useRestoreAllAgents";
import useForceDeleteAgent from "../../hooks/agents/useForceDeleteAgent";
import useForceDeleteAllAgents from "../../hooks/agents/useForceDeleteAllAgents";

import useGetCountriesForSelect from "../../hooks/countries/useGetCountriesForSelect";
import useGetTermOfPaymentsForSelect from "../../hooks/termOfPayments/useGetTermOfPaymentsForSelect";

import useTableActions from "../../hooks/common/useTableActions";
import TableLayout from "../global/Table/TableLayout";
import AgentsTableRow from "./Table/AgentsTableRow";
import AgentsTableColumns from "./Table/AgentsTableColumns";
import ArchiveButton from "../global/Table/ArchiveButton";
import toast from "react-hot-toast";
import { usePermissions } from "../../auth/PermissionsContext";

const AgentsTable = () => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng === "ar";
    const { has } = usePermissions();

    const perms = useMemo(
        () => ({
            list: has("agents_list"),
            create: has("agents_create"),
            edit: has("agents_edit"),
            delete: has("agents_delete"),
            forceDelete: has("agents_force_delete"),
            archiveList: has("agents_archive_list"),
            restore: has("agents_restore"),
            restoreAll: has("agents_restore-all-table"),
            deleteAll: has("agents_delete-all-table"),
            massForceDeleteAll: has("agents_mass_force_delete-all-table"),
        }),
        [has]
    );

    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    const isArchive = location.pathname.includes("/agents/archive");

    // CRUD Hooks
    const { deleteAgent, isDelete } = useDeleteAgent();
    const { deleteAllAgents, isDeleteAll } = useDeleteAllAgents();
    const { restoreAgent, isRestore } = useRestoreAgent();
    const { restoreAllAgents, isRestoreAll } = useRestoreAllAgents();
    const { forceDeleteAgent, isForceDelete } = useForceDeleteAgent();
    const { forceDeleteAllAgents, isForceDeleteAll } = useForceDeleteAllAgents();

    // Lookups for filters
    const { options: countriesOptions, isLoading: isCountriesLoading } = useGetCountriesForSelect();
    const { forSelect: termOfPaymentsOptions, isLoading: isTermsLoading } = useGetTermOfPaymentsForSelect();

    // Table Actions Hook
    const {
        page,
        perPage,
        search,
        sortBy,
        sortDir,
        selectedIds,
        menuAnchorEl,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleEdit,
        handleMenuDelete,
        handleMenuRestore,
        handleBulkDelete,
        handlePageChange,
        handlePerPageChange,
        setSelectedIds,
    } = useTableActions({
        basePath: "/agents",
        deleteSingle: deleteAgent,
        deleteAll: deleteAllAgents,
        restoreSingle: restoreAgent,
        restoreAll: restoreAllAgents,
        forceDeleteSingle: forceDeleteAgent,
        forceDeleteAll: forceDeleteAllAgents,
        isArchive,
    });

    // Local Filters
    const [statusFilter, setStatusFilter] = React.useState("");
    const [countryFilter, setCountryFilter] = React.useState("");
    const [termFilter, setTermFilter] = React.useState("");

    const filters = useMemo(
        () => ({
            search: search || undefined,
            status: statusFilter || undefined,
            country_id: countryFilter || undefined,
            term_of_payment_id: termFilter || undefined,
            sort_by: sortBy,
            sort_dir: sortDir,
            ...(isArchive ? { only_trashed: true } : {}),
        }),
        [search, statusFilter, countryFilter, termFilter, sortBy, sortDir, isArchive]
    );

    // Map lookups for the filter configuration
    const countryFilterOptions = useMemo(() => {
        const defaultOptions = [{ value: "", label: { ar: "الكل", en: "All" } }];
        if (countriesOptions) {
            return [
                ...defaultOptions,
                ...(countriesOptions || [])
            ];
        }
        return defaultOptions;
    }, [countriesOptions]);

    const termFilterOptions = useMemo(() => {
        const defaultOptions = [{ value: "", label: { ar: "الكل", en: "All" } }];
        if (termOfPaymentsOptions) {
            return [
                ...defaultOptions,
                ...(termOfPaymentsOptions || [])
            ];
        }
        return defaultOptions;
    }, [termOfPaymentsOptions]);

    const filterConfig = useMemo(
        () => [
            {
                type: "select",
                name: "status",
                label: { ar: "الحالة", en: "Status" },
                options: [
                    { value: "", label: { ar: "الكل", en: "All" } },
                    { value: "active", label: { ar: "نشط", en: "Active" } },
                    { value: "disabled", label: { ar: "معطل", en: "Disabled" } },
                ],
                value: statusFilter,
                onChange: setStatusFilter,
            },
            {
                type: "select",
                name: "country_id",
                label: { ar: "الدولة", en: "Country" },
                options: countryFilterOptions,
                value: countryFilter,
                onChange: setCountryFilter,
                props: { disabled: isCountriesLoading },
            },
            {
                type: "select",
                name: "term_of_payment_id",
                label: { ar: "طريقة الدفع", en: "Term of Payment" },
                options: termFilterOptions,
                value: termFilter,
                onChange: setTermFilter,
                props: { disabled: isTermsLoading },
            },
        ],
        [
            statusFilter,
            countryFilter,
            termFilter,
            countryFilterOptions,
            termFilterOptions,
            isCountriesLoading,
            isTermsLoading,
        ]
    );

    const handleClearAllFilters = () => {
        setStatusFilter("");
        setCountryFilter("");
        setTermFilter("");
    };

    const { isLoading, error, agents, meta } = useGetAgents(page, perPage, filters);
    const items = useMemo(() => agents || [], [agents]);

    const rowMenuDisabled = isArchive ? (!perms.restore && !perms.forceDelete) : (!perms.edit && !perms.delete);

    const renderRow = (params) => (
        <AgentsTableRow
            key={params.item.id}
            agent={params.item}
            index={params.index}
            isDark={isDark}
            page={params.page}
            perPage={params.perPage}
            sortBy={params.sortBy}
            sortDir={params.sortDir}
            totalItems={items.length}
            selectedIds={selectedIds}
            onRowCheckboxClick={handleRowCheckboxClick}
            onMenuOpen={handleMenuOpen}
            isArchive={isArchive}
            menuDisabled={rowMenuDisabled}
        />
    );

    const bulkDeleteDisabled = isArchive ? !perms.massForceDeleteAll : !perms.deleteAll;

    const title = isArabic ? "الوكلاء" : "Agents";
    const subtitle = isArchive
        ? isArabic
            ? "عرض الوكلاء المحذوفين (الأرشيف)"
            : "Archived agents (soft deleted)"
        : isArabic
            ? "إدارة الوكلاء المتاحين في النظام"
            : "Manage available agents in the system";

    const addButtonDisabled = isArchive
        ? !perms.restoreAll || selectedIds.length === 0
        : !perms.create;

    const addButtonLabel = isArchive
        ? isRestoreAll
            ? isArabic
                ? "جاري الاستعادة..."
                : "Restoring..."
            : isArabic
                ? "استعادة المحدد"
                : "Restore selected"
        : isArabic
            ? "إضافة وكيل"
            : "Create new agent";

    const addButtonAction = isArchive
        ? () => {
            if (addButtonDisabled) {
                toast.error(isArabic ? "ليس لديك صلاحية للاستعادة" : "No restore permission");
                return;
            }
            if (isRestoreAll) return;
            if (!selectedIds.length) {
                toast.error(isArabic ? "الرجاء تحديد العناصر المراد استرجاعها أولاً" : "Please select items to restore first");
                return;
            }
            restoreAllAgents({ items: selectedIds });
            setSelectedIds([]);
        }
        : () => {
            if (addButtonDisabled) {
                toast.error(isArabic ? "ليس لديك صلاحية للإنشاء" : "No create permission");
                return;
            }
            navigate("/agents/create");
        };

    return (
        <>
            {!isArchive && (
                <Stack direction="row" justifyContent={isArabic ? "flex-start" : "flex-end"} sx={{ mb: 1 }}>
                    <ArchiveButton
                        to="/agents/archive"
                        visible={perms.archiveList}
                        disabled={!perms.archiveList}
                        archiveLabelAr="الأرشيف"
                        archiveLabelEn="Archive"
                    />
                </Stack>
            )}
            {isArchive && (
                <Stack direction="row" justifyContent={isArabic ? "flex-start" : "flex-end"} sx={{ mb: 1 }}>
                    <ArchiveButton
                        to="/agents"
                        visible={true}
                        archiveLabelAr="العودة للقائمة"
                        archiveLabelEn="Back to list"
                    />
                </Stack>
            )}
            <TableLayout
                title={title}
                subtitle={subtitle}
                search={search}
                onSearchChange={setSearch}
                addButtonLabel={addButtonLabel}
                onAddClick={addButtonAction}
                addButtonVisible={true}
                isLoading={isLoading}
                error={error}
                items={items}
                meta={meta}
                page={page}
                perPage={perPage}
                onPageChange={handlePageChange}
                onPerPageChange={handlePerPageChange}
                sortBy={sortBy}
                sortDir={sortDir}
                onRequestSort={handleRequestSort}
                columns={AgentsTableColumns}
                renderRow={renderRow}
                menuAnchorEl={menuAnchorEl}
                openMenu={Boolean(menuAnchorEl)}
                onMenuOpen={handleMenuOpen}
                onMenuClose={handleMenuClose}
                onMenuDelete={handleMenuDelete}
                onMenuRestore={isArchive ? handleMenuRestore : undefined}
                onMenuEdit={!isArchive ? handleEdit : undefined}
                menuDisableEdit={!isArchive && !perms.edit}
                menuDisableRestore={isArchive && !perms.restore}
                menuDisableDelete={isArchive ? !perms.forceDelete : !perms.delete}
                isDeletingSingle={isArchive ? isRestore || isForceDelete : isDelete}
                selectedIds={selectedIds}
                onSelectAllClick={(e) => handleSelectAllClick(e, items)}
                onRowCheckboxClick={handleRowCheckboxClick}
                onBulkDelete={handleBulkDelete}
                bulkDeleteDisabled={bulkDeleteDisabled}
                isBulkDeleting={isArchive ? isForceDeleteAll : isDeleteAll}
                addButtonDisabled={addButtonDisabled}
                addButtonTooltip={
                    addButtonDisabled
                        ? isArabic
                            ? "لا تملك صلاحية لتنفيذ هذا الإجراء"
                            : "You don't have permission"
                        : undefined
                }
                emptyLabelAr={isArchive ? "لا يوجد وكلاء في الأرشيف" : "لا يوجد وكلاء"}
                emptyLabelEn={isArchive ? "No archived agents" : "No agents"}
                loadErrorAr="حدث خطاء اثناء تحميل الوكلاء"
                loadErrorEn="Failed to load agents"
                filters={filterConfig}
                onClearAllFilters={handleClearAllFilters}
            />
        </>
    );
};

export default AgentsTable;
